import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { Briefcase, FileText, Users, MessageSquare, ArrowRight, PlusCircle, TrendingUp } from 'lucide-react';

export default function ClientDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOpportunities, setRecentOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, oppsRes] = await Promise.all([
          api.get('/stats/dashboard'),
          api.get('/opportunities/my')
        ]);
        setStats(statsRes.data);
        setRecentOpportunities(oppsRes.data?.slice(0, 5) || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D7377]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  const statCards = [
    { label: 'Messages', value: stats?.unread_messages || 0, icon: MessageSquare, color: '#0EA5E9', link: '/client/messages' },
    { label: 'Total Opportunities', value: stats?.total_opportunities || 0, icon: Briefcase, color: '#0D7377' },
    { label: 'Open', value: stats?.open_opportunities || 0, icon: TrendingUp, color: '#10B981' },
    { label: 'Applications', value: stats?.total_applications || 0, icon: FileText, color: '#6366F1' },
    { label: 'Pending Review', value: stats?.pending_applications || 0, icon: Users, color: '#F59E0B' },
  ];
  
  return (
    <DashboardLayout title={`Welcome back, ${user?.name?.split(' ')[0]}!`}>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, i) => {
          const Card = stat.link ? Link : 'div';
          const cardProps = stat.link ? { to: stat.link } : {};
          return (
            <Card 
              key={i}
              {...cardProps}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`bg-white p-6 border border-neutral-200 hover-lift ${stat.link ? 'cursor-pointer' : ''}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div 
                  className="w-10 h-10 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}15` }}
                >
                  <stat.icon size={20} style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
              <p className="text-sm text-neutral-500">{stat.label}</p>
            </Card>
          );
        })}
      </div>
      
      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Link
          to="/client/opportunities/new"
          data-testid="quick-action-post"
          className="bg-[#E6F3F4] p-6 border border-[#BCE3E4] hover:border-[#0D7377] transition-colors group"
        >
          <div className="flex items-center gap-3 mb-2">
            <PlusCircle className="text-[#0D7377]" size={24} />
            <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>
              Post New Opportunity
            </h3>
          </div>
          <p className="text-neutral-600 text-sm mb-4">
            Create a new research opportunity and attract qualified researchers.
          </p>
          <span className="text-[#0D7377] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            Create Opportunity <ArrowRight size={16} />
          </span>
        </Link>
        
        <Link
          to="/client/profiles"
          data-testid="quick-action-browse"
          className="bg-neutral-100 p-6 border border-neutral-200 hover:border-neutral-400 transition-colors group"
        >
          <div className="flex items-center gap-3 mb-2">
            <Users className="text-neutral-700" size={24} />
            <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>
              Browse Researchers
            </h3>
          </div>
          <p className="text-neutral-600 text-sm mb-4">
            Discover talented researchers with the skills you need.
          </p>
          <span className="text-neutral-800 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            View Profiles <ArrowRight size={16} />
          </span>
        </Link>
      </div>
      
      {/* Recent Opportunities */}
      <div className="bg-white border border-neutral-200">
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>
            Your Opportunities
          </h2>
          <Link 
            to="/client/opportunities"
            className="text-sm text-[#0D7377] font-medium hover:underline"
          >
            View All
          </Link>
        </div>
        
        {recentOpportunities.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-neutral-500 mb-4">You haven't posted any opportunities yet.</p>
            <Link
              to="/client/opportunities/new"
              className="btn-client px-6 py-3 inline-flex items-center gap-2"
            >
              <PlusCircle size={18} />
              Post Your First Opportunity
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-neutral-200">
            {recentOpportunities.map((opp) => (
              <Link
                key={opp.opportunity_id}
                to={`/opportunity/${opp.opportunity_id}`}
                data-testid={`opportunity-${opp.opportunity_id}`}
                className="block p-6 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-neutral-900 truncate">
                        {opp.title}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${
                        opp.status === 'open' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        {opp.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {opp.skills_needed?.slice(0, 3).map((skill, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-[#0D7377]">
                      {opp.application_count || 0}
                    </p>
                    <p className="text-xs text-neutral-500">
                      Applications
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      
      {/* Messages indicator */}
      {stats?.unread_messages > 0 && (
        <Link
          to="/client/messages"
          data-testid="unread-messages-banner"
          className="mt-6 bg-[#0D7377] text-white p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <MessageSquare size={20} />
            <span className="font-medium">
              You have {stats.unread_messages} unread message{stats.unread_messages > 1 ? 's' : ''}
            </span>
          </div>
          <ArrowRight size={20} />
        </Link>
      )}
    </DashboardLayout>
  );
}
